import { FC, useEffect, useState } from 'react';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import { Alert, Button } from 'react-daisyui';
import { useAuthContext } from '@/context/AuthContext.tsx';
import useCurrentUser from '@/api/useCurrentUser.ts';
import useUpdateUserCredentials from '@/api/useUpdateUserCredentials.ts';
import useUpdateSubjects from '@/api/useUpdateSubjects.ts';
import useDeleteAccount from '@/api/useDeleteAccount.ts';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import Modal from '@/components/modals/Modal/Modal.tsx';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ArrowDownIcon } from '@heroicons/react/20/solid';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';

const accountSettingsSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email('Invalid email address'),
  password: z.string().optional(),
  predictedGrade: z.string().optional(),
  aspirationalGrade: z.string().optional(),
});

type AccountSettingsSchema = z.infer<typeof accountSettingsSchema>;

const AccountSettingsPage: FC = () => {
  const { logout, session } = useAuthContext();
  const { data: currentUser } = useCurrentUser();
  const {
    mutateAsync: updateCredentials,
    isPending: isUpdatingCredentials,
    error: credentialsError,
    isError: isCredentialsError,
  } = useUpdateUserCredentials();
  const {
    mutateAsync: updateSubjects,
    isPending: isUpdatingSubjects,
    error: gradesError,
    isError: isGradesError,
  } = useUpdateSubjects();
  const { mutateAsync: deleteAccount, isPending: isDeletingAccount } = useDeleteAccount();

  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<AccountSettingsSchema>({
    resolver: zodResolver(accountSettingsSchema),
    defaultValues: {
      email: session?.user?.email || '',
      password: '',
      predictedGrade: currentUser?.predicted_grade || '',
      aspirationalGrade: currentUser?.aspirational_grade || '',
    },
  });

  // Update form when user data loads
  useEffect(() => {
    if (currentUser && session?.user?.email) {
      reset({
        email: session.user.email,
        password: '',
        predictedGrade: currentUser.predicted_grade || '',
        aspirationalGrade: currentUser.aspirational_grade || '',
      });
    }
  }, [currentUser, session?.user?.email, reset]);

  const handleUpdateEmailPassword = (data: AccountSettingsSchema) => {
    void (async () => {
      try {
        const updates: { email?: string; password?: string } = {};

        if (dirtyFields.email && data.email) {
          updates.email = data.email;
        }

        if (dirtyFields.password && data.password) {
          updates.password = data.password;
        }

        if (Object.keys(updates).length > 0) {
          await updateCredentials(updates);

          let message = '';
          if (updates.email && updates.password) {
            message = 'Email and password updated successfully. Please check your new email for confirmation.';
          } else if (updates.email) {
            message = 'Email update initiated. Please check your new email for confirmation.';
          } else if (updates.password) {
            message = 'Password updated successfully.';
          }

          setSuccessMessage(message);
          setIsSuccessModalOpen(true);
          reset(data);
        }
      } catch (error) {
        console.error('Failed to update credentials:', error);
        const errorMsg = error instanceof Error ? error.message : 'Failed to update credentials. Please try again.';
        setErrorMessage(errorMsg);
        setIsErrorModalOpen(true);
      }
    })();
  };

  const handleUpdateGrades = (data: AccountSettingsSchema) => {
    void (async () => {
      try {
        const allSubjects = currentUser?.subjects || [];
        await updateSubjects({
          subjects: allSubjects,
          predicted_grade: data.predictedGrade,
          aspirational_grade: data.aspirationalGrade,
        });
        setSuccessMessage('Grades updated successfully.');
        setIsSuccessModalOpen(true);
        reset(data);
      } catch (error) {
        console.error('Failed to update grades:', error);
        const errorMsg = error instanceof Error ? error.message : 'Failed to update grades. Please try again.';
        setErrorMessage(errorMsg);
        setIsErrorModalOpen(true);
      }
    })();
  };

  const handleDeleteAccount = () => {
    void (async () => {
      try {
        // Delete account - this will delete both auth user and users table data
        await deleteAccount();
        setIsDeleteModalOpen(false);
        setSuccessMessage('Account deleted successfully.');
        setIsSuccessModalOpen(true);
      } catch (error) {
        console.error('Failed to delete account:', error);
        const errorMsg = error instanceof Error ? error.message : 'Failed to delete account. Please try again.';
        setErrorMessage(errorMsg);
        setIsErrorModalOpen(true);
        setIsDeleteModalOpen(false);
      }
    })();
  };

  return (
    <>
      <PageHeader title="Account Settings" />
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap gap-6">
          {/* Email & Password Section */}
          <div className="bg-white rounded-[20px] border border-[#CCCCCC] border-opacity-50 p-6 w-full max-w-[400px]">
            <form onSubmit={handleSubmit(handleUpdateEmailPassword)} className="flex flex-col gap-6">
              {isCredentialsError && <Alert status="error">{credentialsError?.message}</Alert>}
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, value }, formState: { errors } }) => (
                  <TextInput
                    error={errors.email?.message}
                    value={value}
                    onChange={onChange}
                    label="Email"
                    placeholder="Enter email"
                    type="email"
                  />
                )}
              />
              <Controller
                control={control}
                name="password"
                render={({ field: { onChange, value }, formState: { errors } }) => (
                  <TextInput
                    error={errors.password?.message}
                    value={value}
                    onChange={onChange}
                    label="Password"
                    placeholder="Enter password"
                    type="password"
                  />
                )}
              />
              <LoadingButton
                loading={isUpdatingCredentials}
                type="submit"
                color="secondary"
                disabled={!dirtyFields.email && !dirtyFields.password}
                className="w-full max-w-[216px] self-center"
              >
                Edit
              </LoadingButton>
            </form>
          </div>

          {/* Grades Section */}
          <div className="bg-white rounded-[20px] border border-[#CCCCCC] border-opacity-50 p-6 w-full max-w-[400px]">
            <form onSubmit={handleSubmit(handleUpdateGrades)} className="flex flex-col gap-6">
              {isGradesError && <Alert status="error">{gradesError?.message}</Alert>}
              <Controller
                control={control}
                name="predictedGrade"
                render={({ field: { onChange, value }, formState: { errors } }) => (
                  <div className="flex flex-col gap-2">
                    <label className="text-base font-medium">Predicted Grade</label>
                    <div className="relative">
                      <SelectInput
                        value={value || ''}
                        onChange={onChange}
                        options={[1, 2, 3, 4, 5, 6, 7, 8, 9].map((grade) => ({
                          value: grade.toString(),
                          label: grade.toString(),
                        }))}
                        className="text-base font-normal"
                        labelClassName="pr-10 [&>svg]:hidden"
                        bordered
                      />
                      <ArrowDownIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600" />
                    </div>
                    {errors.predictedGrade && (
                      <span className="text-sm text-error">{errors.predictedGrade.message}</span>
                    )}
                  </div>
                )}
              />
              <Controller
                control={control}
                name="aspirationalGrade"
                render={({ field: { onChange, value }, formState: { errors } }) => (
                  <div className="flex flex-col gap-2">
                    <label className="text-base font-medium">Aspirational grade</label>
                    <div className="relative">
                      <SelectInput
                        value={value || ''}
                        onChange={onChange}
                        options={[1, 2, 3, 4, 5, 6, 7, 8, 9].map((grade) => ({
                          value: grade.toString(),
                          label: grade.toString(),
                        }))}
                        className="text-base font-normal"
                        labelClassName="pr-10 [&>svg]:hidden"
                        bordered
                      />
                      <ArrowDownIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600" />
                    </div>
                    {errors.aspirationalGrade && (
                      <span className="text-sm text-error">{errors.aspirationalGrade.message}</span>
                    )}
                  </div>
                )}
              />
              <LoadingButton
                loading={isUpdatingSubjects}
                type="submit"
                disabled={!dirtyFields.predictedGrade && !dirtyFields.aspirationalGrade}
                color="primary"
                className="w-full max-w-[216px] text-white hover:bg-dark-hover-secondary border-none self-center"
              >
                Edit
              </LoadingButton>
            </form>
          </div>

          {/* Exam Board Section */}
          <div className="bg-white rounded-[20px] border border-[#CCCCCC] border-opacity-50 p-6 w-full max-w-[400px]">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-base font-medium">Exam Board</label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-base font-normal text-gray-700">Pearson Edexcel</p>
                  <p className="text-sm text-gray-500 mt-1">(Currently the only supported exam board)</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4 max-w-[368px] w-full">
          <Button
            onClick={() => void logout()}
            color="ghost"
            className="w-full min-h-[48px] text-sm border border-[#2B3440] text-[#2B3440] bg-white hover:bg-gray-50 rounded-xl font-semibold"
          >
            Log Out
          </Button>
          <Button
            onClick={() => setIsDeleteModalOpen(true)}
            color="ghost"
            className="w-full min-h-[48px] text-sm border border-[#EF4444] text-[#EF4444] bg-white hover:bg-red-50 rounded-xl font-semibold"
          >
            Delete Account
          </Button>
        </div>
      </div>
      {/* Success Modal */}
      <Modal.Wrapper isOpen={isSuccessModalOpen} onClose={() => setIsSuccessModalOpen(false)} showCloseButton>
        <div className="flex flex-col items-center gap-4 py-6">
          <CheckCircleIcon className="w-16 h-16 text-success" />
          <h2 className="text-2xl font-varela font-normal">Success!</h2>
          <p className="text-center text-text-primary">{successMessage}</p>
        </div>
        <Modal.Nav>
          {({ handleClose }) => (
            <Button color="primary" onClick={handleClose} className="w-full">
              Close
            </Button>
          )}
        </Modal.Nav>
      </Modal.Wrapper>

      {/* Error Modal */}
      <Modal.Wrapper isOpen={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)} showCloseButton>
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="w-16 h-16 rounded-full bg-error bg-opacity-10 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-error"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-varela font-normal">Error</h2>
          <p className="text-center text-text-primary">{errorMessage}</p>
        </div>
        <Modal.Nav>
          {({ handleClose }) => (
            <Button color="error" onClick={handleClose} className="w-full">
              Close
            </Button>
          )}
        </Modal.Nav>
      </Modal.Wrapper>

      {/* Delete Account Confirmation Modal */}
      <Modal.Wrapper isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} closeable={false}>
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="w-16 h-16 rounded-full bg-error bg-opacity-10 flex items-center justify-center">
            <svg
              className="w-10 h-10 text-error"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-varela font-normal">Delete Account</h2>
          <p className="text-center text-text-secondary px-4">
            Are you sure you want to delete your account? This action cannot be undone and all your data will be
            permanently deleted.
          </p>
        </div>
        <Modal.Nav>
          {({ handleClose }) => (
            <div className="flex gap-4 w-full">
              <Button
                color="ghost"
                onClick={handleClose}
                disabled={isDeletingAccount}
                className="flex-1 border border-gray-300"
              >
                Cancel
              </Button>
              <LoadingButton loading={isDeletingAccount} color="error" onClick={handleDeleteAccount} className="flex-1">
                Delete Account
              </LoadingButton>
            </div>
          )}
        </Modal.Nav>
      </Modal.Wrapper>
    </>
  );
};

export default AccountSettingsPage;
